import { PATIENT_CONTACT_CHART_LABEL } from '../../hooks/useMedplumPatientContacts';
import type { ClientOverviewContactRow } from '../clients/utils/getClientOverviewContacts';

export type CaseloadContactType = 'emergency-contact' | 'related-person' | 'guarantor';

export const CASELOAD_CONTACT_TYPE_ORDER: readonly CaseloadContactType[] = [
  'emergency-contact',
  'related-person',
  'guarantor',
] as const;

export const CASELOAD_CONTACT_TYPE_LABELS: Record<CaseloadContactType, string> = {
  'emergency-contact': 'Emergency contact',
  'related-person': 'Related Person',
  'guarantor': 'Guarantor',
};

export const CASELOAD_CONTACT_TYPE_OPTIONS = CASELOAD_CONTACT_TYPE_ORDER.map((value) => ({
  value,
  label: CASELOAD_CONTACT_TYPE_LABELS[value],
}));

const EMERGENCY_ROLE = /emergency/i;

/**
 * Caseload Contacts "Contact type" values, derived from the row's FHIR source:
 * Patient.contact (chart) → Emergency contact, RelatedPerson → Related Person,
 * EoC guarantor extension → Guarantor. A merged row can have more than one type.
 */
export function caseloadContactTypesFromRow(row: ClientOverviewContactRow): CaseloadContactType[] {
  const types = new Set<CaseloadContactType>();

  if (row.guarantorBillingProfileClientId) {
    types.add('guarantor');
  }
  if (row.dedupeKey.startsWith('rp:')) {
    types.add('related-person');
  }
  if (row.dedupeKey.startsWith('pc:')) {
    types.add('emergency-contact');
  }

  for (const segment of row.segments) {
    if (segment.episodeOfCareLabel === PATIENT_CONTACT_CHART_LABEL) {
      types.add('emergency-contact');
    }
    if (segment.roleLabel === 'Guarantor') {
      types.add('guarantor');
    }
    if (EMERGENCY_ROLE.test(segment.roleLabel)) {
      types.add('emergency-contact');
    }
    if (
      segment.episodeOfCareLabel !== PATIENT_CONTACT_CHART_LABEL &&
      segment.roleLabel !== 'Guarantor'
    ) {
      types.add('related-person');
    }
  }

  return CASELOAD_CONTACT_TYPE_ORDER.filter((type) => types.has(type));
}

export function formatCaseloadContactTypes(types: CaseloadContactType[]): string {
  return types.map((type) => CASELOAD_CONTACT_TYPE_LABELS[type]).join(', ');
}

export function caseloadContactRelationships(row: ClientOverviewContactRow): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const segment of row.segments) {
    const label = segment.roleLabel?.trim();
    if (!label || seen.has(label)) continue;
    seen.add(label);
    ordered.push(label);
  }
  if (ordered.length > 0) return ordered;
  return (row.rolesSummary ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part && part !== '—');
}

export function contactRowMatchesTypeFilter(
  types: CaseloadContactType[],
  selected: string[],
): boolean {
  if (selected.length === 0) return false;
  return types.some((type) => selected.includes(type));
}

export function contactRowMatchesRelationshipFilter(
  relationships: string[],
  selected: string[],
): boolean {
  if (selected.length === 0) return false;
  return relationships.some((rel) => selected.includes(rel));
}

export function contactRowMatchesSearch(
  row: Pick<ClientOverviewContactRow, 'displayName' | 'rolesSummary' | 'phone' | 'email'> & {
    subjectClientName?: string;
  },
  query: string,
  contactTypeSummary: string,
): boolean {
  const q = query.toLowerCase();
  return [
    row.subjectClientName,
    row.displayName,
    row.rolesSummary,
    contactTypeSummary,
    row.phone,
    row.email,
  ].some((value) => (value ?? '').toLowerCase().includes(q));
}

export { PATIENT_CONTACT_CHART_LABEL };
